import { prop } from "ramda";
import React, { useEffect } from "react";

import {
  Box,
  Button,
  Callout,
  Checkbox,
  Dialog,
  Grouper,
  Intent,
  Label,
  Text,
  TextInput
} from "@blasterjs/core";

import ReasonForChange from "../components/ReasonForChange";

import { DialogBody, DialogFooter, DialogHeader } from "../components/DialogLayout";
import Select, { SelectOfType } from "../components/Select";

import {
  AdjudicationParadigm,
  CaseStatus,
  formatCaseStatus,
  Image,
  isBiopsyLevelAdjudication,
  isSingleReaderParadigm,
  readerPickerState,
  ReaderStudyStats,
  userLabelShort
} from "../models";
import { areDifferent } from "../utils";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  addReader,
  assignImage,
  CaseDialogMode,
  CaseDialogState,
  CaseForm,
  caseSearchAdjudicatorsRequest,
  caseSearchReadersRequest,
  caseSuggestReadersRequest,
  changeCase,
  clearAdjudicator,
  closeCaseDialog,
  createCaseRequest,
  dropReader,
  editCaseRequest,
  imagesSearch,
  selectAdjudicator,
  setAdjudicatorSearch,
  toggleBiopsyImages,
  unassignImage
} from "../slices/caseDialog";
import { RolePermissions } from "../permissions";
import styled from "styled-components";

const RfcBox = styled.div`
  padding-top: 10px;
`;

const BoldText = styled.span`
  font-weight: bold;
`;

interface CaseValidations {
  readonly visitIdLength: string | null;
  readonly visitIdFormat: string | null;
  readonly endoProcIdLength: string | null;
  readonly endoProcIdFormat: string | null;
  readonly histoProcIdLength: string | null;
  readonly histoProcIdFormat: string | null;
  readonly subjectIdLength: string | null;
  readonly subjectIdFormat: string | null;
  readonly siteIdLength: string | null;
  readonly siteIdFormat: string | null;
  readonly errorsExist: boolean;
}

function validateCaseForm(caseDialog: CaseDialogState): CaseValidations {
  function checkTextInputFormat(text: string) {
    if (text.length == 0) {
      return true;
    }

    // Regex pattern that allows:
    // - Alphanumeric characters (a-z, A-Z, 0-9)
    // - Common special characters: space, hyphen, underscore, period, comma, parentheses
    // - Common punctuation: !?@#$%&*
    const pattern = /^[a-zA-Z0-9\s\-_.,'"/()@#%&*]+$/;

    return pattern.test(text);
  }

  const visitIdTooLong = caseDialog.histoCase.data.visitId.value.length > 75;
  const visitIdFormatError = !checkTextInputFormat(caseDialog.histoCase.data.visitId.value);
  const endoProcIdTooLong = caseDialog.histoCase.data.procId.value.length > 50;
  const endoProcIdFormatError = !checkTextInputFormat(caseDialog.histoCase.data.procId.value);
  const histoProcedureId = caseDialog?.histoCase?.data?.histoProcedureId.value ?? "";
  const histoProcIdTooLong = histoProcedureId.length > 50;
  const histoProcIdFormatError = !checkTextInputFormat(histoProcedureId);
  const subjectIdTooLong = caseDialog.histoCase.data.subjectId.value.length > 30;
  const subjectIdFormatError = !checkTextInputFormat(caseDialog.histoCase.data.subjectId.value);
  const siteId = caseDialog?.histoCase?.data?.siteId.value ?? "";
  const siteIdTooLong = siteId.length > 30;
  const siteIdFormatError = !checkTextInputFormat(siteId);

  return {
    visitIdLength: visitIdTooLong ? "Visit ID must be less than 75 characters" : "",
    visitIdFormat: visitIdFormatError ? "Visit ID must be alphanumeric" : "",
    endoProcIdLength: endoProcIdTooLong ? "Endo Procedure ID must be less than 50 characters" : "",
    endoProcIdFormat: endoProcIdFormatError ? "Endo Procedure ID must be alphanumeric" : "",
    histoProcIdLength: histoProcIdTooLong
      ? "Histo Procedure ID must be less than 50 characters"
      : "",
    histoProcIdFormat: histoProcIdFormatError ? "Histo Procedure ID must be alphanumeric" : "",
    subjectIdLength: subjectIdTooLong ? "Subject ID must be less than 30 characters" : "",
    subjectIdFormat: subjectIdFormatError ? "Subject ID must be alphanumeric" : "",
    siteIdLength: siteIdTooLong ? "Site ID must be less than 30 characters" : "",
    siteIdFormat: siteIdFormatError ? "Site ID must be alphanumeric" : "",
    errorsExist:
      visitIdTooLong ||
      visitIdFormatError ||
      endoProcIdTooLong ||
      endoProcIdFormatError ||
      histoProcIdTooLong ||
      histoProcIdFormatError ||
      subjectIdTooLong ||
      subjectIdFormatError ||
      siteIdTooLong ||
      siteIdFormatError
  };
}

function formatReaderStats(stats: ReaderStudyStats): JSX.Element {
  return (
    <>
      <BoldText>{userLabelShort(stats.reader)}</BoldText>&nbsp;({stats.numReadCases}/
      {stats.numTotalCases} cases read)
    </>
  );
}

const SelectReaders = Select as SelectOfType<ReaderStudyStats>;
const SelectAdjudicator = Select as SelectOfType<ReaderStudyStats>;
const SelectImages = Select as SelectOfType<Image>;

const CaseDialog = () => {
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector(state => state.auth.loggedInUser);

  const caseDialog = useAppSelector(state => state.caseDialog);
  const caseValidations = useAppSelector(state => validateCaseForm(state.caseDialog));

  useEffect(() => {
    if (caseDialog.histoCase.data.subjectId.value) {
      dispatch(caseSuggestReadersRequest(caseDialog.histoCase.data.subjectId.value));
    }
  }, [caseDialog.savedHistoCase?.caseWithStatus.id]);

  const updateCaseField = <
    Key extends keyof CaseForm<CaseDialogMode>,
    PartialUpdate extends Partial<CaseForm<CaseDialogMode>[Key]>
  >(
    caseKey: Key,
    value: PartialUpdate
  ) => {
    dispatch(
      changeCase({
        ...caseDialog.histoCase.data,
        [caseKey]: {
          ...caseDialog.histoCase.data[caseKey],
          ...value
        }
      })
    );
  };

  const onSubjectIdChange = (key: keyof CaseForm<CaseDialogMode>) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const subjectId = e.currentTarget.value.trim();
    dispatch(caseSuggestReadersRequest(subjectId));
    updateCaseField(key, { value: subjectId });
  };

  const updateCaseFieldWithValue = (key: keyof CaseForm<CaseDialogMode>) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => updateCaseField(key, { value: e.currentTarget.value });
  const updateCaseFieldWithReasonForChange = (key: keyof CaseForm<CaseDialogMode>) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => updateCaseField(key, { reasonForChange: e.currentTarget.value });
  const onReaderNameChange = (value: string) => {
    dispatch(caseSearchReadersRequest({ studyId: caseDialog.studyId, name: value }));
  };
  const onClickSuggestedReader = (reader: ReaderStudyStats) => () => onSelectReader(reader);
  const onSelectReader = (reader: ReaderStudyStats) => {
    dispatch(addReader(reader));
  };
  const onUnassignReader = (reader: ReaderStudyStats) => {
    dispatch(dropReader(reader));
  };
  const onImageNameChange = (value: string) => {
    dispatch(imagesSearch(value));
  };
  const onSelectImage = (image: Image) => {
    dispatch(assignImage(image));
  };
  const onUnassignImage = (image: Image) => {
    dispatch(unassignImage(image));
  };
  const closeDialog = () => {
    dispatch(closeCaseDialog());
  };
  const onSave = () => {
    dispatch(
      caseDialog.mode === CaseDialogMode.OpenForCreate ? createCaseRequest() : editCaseRequest()
    );
  };
  const caseStatus = caseDialog.savedHistoCase && caseDialog.savedHistoCase.caseWithStatus.status;
  const imageAssignmentDisabled =
    caseDialog.mode !== CaseDialogMode.OpenForCreate &&
    !(caseStatus == CaseStatus.PendingLabQC || caseStatus == CaseStatus.PendingQC);
  const disabledText = imageAssignmentDisabled
    ? `Images can only be assigned to cases in ${formatCaseStatus(CaseStatus.PendingQC)}`
    : undefined;
  // FE-03: a None-paradigm study is single-reader, so once one CR is assigned the picker is disabled
  // (mirrors the backend BE-02 rule). Multi-reader paradigms have no client-side limit.
  const assignedReaderCount = caseDialog.histoCase.data
    ? caseDialog.histoCase.data.readers.value.length
    : 0;
  // Skip the cap while the paradigm is unknown (null, study still loading) so we don't wrongly lock
  // the picker for a multi-reader study; use the shared predicate so this can't drift from the
  // reducer's add-guard (FE-03 / BE-02).
  const singleReaderLimitReached =
    caseDialog.adjudicationParadigm !== null &&
    isSingleReaderParadigm(caseDialog.adjudicationParadigm) &&
    assignedReaderCount >= 1;
  const canManuallyAssignReaders =
    "resource" in loggedInUser &&
    loggedInUser.resource.can([RolePermissions.AP_CaseViewer_ManualCRassignment]);
  const readerPicker = readerPickerState(singleReaderLimitReached, canManuallyAssignReaders);

  // FE-04: the Adjudicator-assignment section is shown for adjudication studies (paradigm != None),
  // once the case exists (edit mode — assignment needs a caseId). For BIOPSY_LEVEL the ISC also
  // picks which biopsies the adjudicator may access.
  const showAdjudicatorSection =
    caseDialog.mode === CaseDialogMode.OpenForEdit &&
    caseDialog.id !== null &&
    caseDialog.adjudicationParadigm !== AdjudicationParadigm.None;
  // Mirror the backend rule (Hybrid is always biopsy-level, Forced honours the mode) via the shared
  // predicate so the picker matches what the assign-adjudicator API requires. Unknown paradigm
  // (null, study still loading) is treated as not-yet-biopsy-level.
  const isBiopsyLevel =
    caseDialog.adjudicationParadigm !== null &&
    isBiopsyLevelAdjudication(caseDialog.adjudicationParadigm, caseDialog.adjudicationMode);
  const assignedReaderIds = caseDialog.histoCase.data.readers.value.map(r => r.reader.id);
  // The currently-selected adjudicator (form state) — a CR cannot be both a reader and the
  // adjudicator on the same case, so each search excludes the other role's picks.
  const selectedAdjudicatorId =
    caseDialog.histoCase.data.adjudicator.value.selected?.reader.id ?? null;
  // The reader search results, excluding anyone already assigned as a reader on this case and the
  // currently-selected adjudicator (so already-picked users don't clutter the search).
  const readerPool = ("resource" in caseDialog.readersSearchResults
    ? caseDialog.readersSearchResults.resource
    : []
  ).filter(
    stats =>
      !assignedReaderIds.includes(stats.reader.id) && stats.reader.id !== selectedAdjudicatorId
  );
  // The adjudicator pool is the study CR search results, excluding CRs already assigned as readers
  // on this case and the CR already selected as the adjudicator (a reader cannot also be the
  // adjudicator on the same case, and the current pick shouldn't reappear in its own search).
  const adjudicatorPool = ("resource" in caseDialog.adjudicatorSearchResults
    ? caseDialog.adjudicatorSearchResults.resource
    : []
  ).filter(
    stats =>
      !assignedReaderIds.includes(stats.reader.id) && stats.reader.id !== selectedAdjudicatorId
  );
  // FE-04: biopsy groupings come from the backend (BE-03, single source of truth).
  const biopsies = caseDialog.biopsies;
  // FE-04: the chosen adjudicator + biopsy selection now live in the case form (applied on Save),
  // shown as the same blue removable X-chip Central Readers get.
  const adjudicatorForm = caseDialog.histoCase.data.adjudicator.value;
  const selectedAdjudicatorItems = adjudicatorForm.selected ? [adjudicatorForm.selected] : [];
  const savedAdjudicatorId = caseDialog.savedHistoCase?.adjudicators[0]?.reader.id ?? null;
  const formAdjudicatorId = adjudicatorForm.selected?.reader.id ?? null;
  // LUC-264: a biopsy-scope-only edit (same adjudicator, different biopsies) is still a change that
  // must capture a reason-for-change. Comparing only the adjudicator user id here left the RFC field
  // hidden and Save enabled while onlyUpdates still diffed the biopsy array and sent the update with
  // a blank reason — persisting a real scope change with no audit reason. Detect the scope diff too
  // (order-insensitive; only meaningful while an adjudicator is selected).
  const savedAdjudicatorImageIds = caseDialog.savedHistoCase?.adjudicatorImageIds ?? [];
  const adjudicatorScopeChanged =
    formAdjudicatorId !== null &&
    areDifferent(savedAdjudicatorImageIds, adjudicatorForm.selectedBiopsyImageIds);
  const adjudicatorChanged = savedAdjudicatorId !== formAdjudicatorId || adjudicatorScopeChanged;
  const onAdjudicatorNameChange = (value: string) => {
    dispatch(setAdjudicatorSearch(value));
    dispatch(caseSearchAdjudicatorsRequest({ studyId: caseDialog.studyId, name: value }));
  };
  const onSelectAdjudicator = (stats: ReaderStudyStats) => dispatch(selectAdjudicator(stats));
  const onClearAdjudicator = () => dispatch(clearAdjudicator());
  const onToggleBiopsy = (imageIds: ReadonlyArray<string>) => () =>
    dispatch(toggleBiopsyImages(imageIds));

  const errorText =
    "errorMessage" in caseDialog.histoCase ? (
      <Box mb={2}>
        <Callout intent={Intent.DANGER}>
          <Text>{caseDialog.histoCase.errorMessage}</Text>
        </Callout>
      </Box>
    ) : null;
  const readerSuggestionText =
    "resource" in caseDialog.readerSuggestions && caseDialog.readerSuggestions.resource.length ? (
      <Text>
        <Text as="i">
          Suggested reader(s) for subject {caseDialog.histoCase.data.subjectId.value}
        </Text>
        :
        <Grouper>
          {caseDialog.readerSuggestions.resource.map(readerStats => (
            <Button
              key={readerStats.reader.id}
              onClick={onClickSuggestedReader(readerStats)}
              // Same gate as the readers picker: quick-add is still a manual CR assignment.
              disabled={!readerPicker.canAdd}
            >
              {userLabelShort(readerStats.reader)}
            </Button>
          ))}
        </Grouper>
      </Text>
    ) : null;

  function fieldSaveableCase(
    fieldName: "procId" | "histoProcedureId" | "subjectId" | "siteId" | "visitId"
  ): boolean {
    const isNewCase: boolean = caseDialog.savedHistoCase == null;
    const newValue = caseDialog.histoCase.data[fieldName].value || "";
    const oldValue = caseDialog.savedHistoCase?.caseWithStatus[fieldName] || "";
    const rfc =
      ("reasonForChange" in caseDialog.histoCase.data[fieldName] &&
        (caseDialog.histoCase.data[fieldName] as any).reasonForChange) ||
      "";
    const updateSavable = oldValue == newValue || (oldValue != newValue && rfc.length > 0);
    return isNewCase || updateSavable;
  }

  function fieldSaveableCaseMany(fieldName: "images" | "readers"): boolean {
    const isNewCase: boolean = caseDialog.savedHistoCase == null;
    const newValue = caseDialog.histoCase.data[fieldName].value || [];
    const oldValue =
      (caseDialog &&
        caseDialog.savedHistoCase &&
        fieldName in caseDialog.savedHistoCase &&
        caseDialog.savedHistoCase[fieldName]) ||
      [];
    const rfc =
      ("reasonForChange" in caseDialog.histoCase.data[fieldName] &&
        (caseDialog.histoCase.data[fieldName] as any).reasonForChange) ||
      "";
    const updateSavable = oldValue == newValue || (oldValue != newValue && rfc.length > 0);
    return isNewCase || updateSavable;
  }

  // FE-04: the adjudicator is a form field applied on Save. Block Save when it changed but its
  // reason-for-change is empty, and when a biopsy-level adjudicator is selected without any biopsies.
  const adjudicatorRfc =
    "reasonForChange" in caseDialog.histoCase.data.adjudicator
      ? (caseDialog.histoCase.data.adjudicator as { reasonForChange?: string }).reasonForChange ??
        ""
      : "";
  const adjudicatorSaveable =
    caseDialog.savedHistoCase == null || !adjudicatorChanged || adjudicatorRfc.length > 0;
  const adjudicatorBiopsyMissing =
    isBiopsyLevel &&
    adjudicatorForm.selected !== null &&
    adjudicatorForm.selectedBiopsyImageIds.length === 0;

  const disableSave: boolean =
    caseValidations.errorsExist ||
    !fieldSaveableCase("procId") ||
    !fieldSaveableCase("histoProcedureId") ||
    !fieldSaveableCase("subjectId") ||
    !fieldSaveableCase("siteId") ||
    !fieldSaveableCase("visitId") ||
    !fieldSaveableCaseMany("images") ||
    !fieldSaveableCaseMany("readers") ||
    !adjudicatorSaveable ||
    adjudicatorBiopsyMissing;

  return (
    <Dialog
      isOpen={
        caseDialog.mode !== CaseDialogMode.Closed &&
        // Avoid flicker by waiting to open dialog until case has loaded
        !("isPending" in caseDialog.histoCase)
      }
      onRequestClose={closeDialog}
      appElementId="root"
      width="500px"
    >
      <DialogHeader
        title={(caseDialog.mode === CaseDialogMode.OpenForCreate ? "Create" : "Edit") + " Case"}
        closeDialog={closeDialog}
      />
      <DialogBody>
        {errorText}
        <Box>
          <Label>
            Endo Procedure ID
            <TextInput
              placeholder="Endo Procedure ID"
              value={caseDialog.histoCase.data.procId.value}
              onChange={updateCaseFieldWithValue("procId")}
            />
            <Box
              style={{
                display: caseValidations.errorsExist ? "block" : "none",
                color: "red",
                fontSize: "10pt"
              }}
            >
              {caseValidations.endoProcIdLength}
              {caseValidations.endoProcIdLength ? <br /> : <></>}
              {caseValidations.endoProcIdFormat}
            </Box>
          </Label>
        </Box>
        {caseDialog.mode === CaseDialogMode.OpenForEdit &&
        caseDialog.savedHistoCase?.caseWithStatus.procId !==
          caseDialog.histoCase.data.procId.value ? (
          <RfcBox>
            <ReasonForChange
              rkey="procId_reason"
              mb={2}
              value={
                "reasonForChange" in caseDialog.histoCase.data.procId
                  ? caseDialog.histoCase.data.procId.reasonForChange
                  : ""
              }
              onChange={updateCaseFieldWithReasonForChange("procId")}
            />
          </RfcBox>
        ) : null}
        {caseDialog.mode === CaseDialogMode.OpenForCreate ? (
          <></>
        ) : (
          <>
            <Box mt={2}>
              <Label>
                Histo Procedure ID
                <TextInput
                  placeholder="Histo Procedure ID"
                  value={caseDialog.histoCase.data.histoProcedureId.value || ""}
                  onChange={updateCaseFieldWithValue("histoProcedureId")}
                />
                <Box
                  className="validationMessage"
                  style={{
                    display: caseValidations.errorsExist ? "block" : "none"
                  }}
                >
                  {caseValidations.histoProcIdLength}
                  {caseValidations.histoProcIdLength ? <br /> : <></>}
                  {caseValidations.histoProcIdFormat}
                </Box>
              </Label>
            </Box>
            {caseDialog.mode === CaseDialogMode.OpenForEdit &&
            caseDialog.savedHistoCase?.caseWithStatus.histoProcedureId !==
              caseDialog.histoCase.data.histoProcedureId.value ? (
              <RfcBox>
                <ReasonForChange
                  rkey="histoProcedureId_reason"
                  mb={2}
                  value={
                    "reasonForChange" in caseDialog.histoCase.data.histoProcedureId
                      ? caseDialog.histoCase.data.histoProcedureId.reasonForChange
                      : ""
                  }
                  onChange={updateCaseFieldWithReasonForChange("histoProcedureId")}
                />
              </RfcBox>
            ) : null}
          </>
        )}
        <Box mt={2}>
          <Label>
            Subject ID
            <br />
            <TextInput
              placeholder="Subject ID"
              value={caseDialog.histoCase.data.subjectId.value}
              onChange={onSubjectIdChange("subjectId")}
            />
            <Box
              style={{
                display: caseValidations.errorsExist ? "block" : "none",
                color: "red",
                fontSize: "10pt"
              }}
            >
              {caseValidations.subjectIdLength}
              {caseValidations.subjectIdLength ? <br /> : <></>}
              {caseValidations.subjectIdFormat}
            </Box>
          </Label>
        </Box>
        {caseDialog.mode === CaseDialogMode.OpenForEdit &&
        caseDialog.savedHistoCase?.caseWithStatus.subjectId !==
          caseDialog.histoCase.data.subjectId.value ? (
          <RfcBox>
            <ReasonForChange
              rkey="subjectId_reason"
              mb={2}
              value={
                "reasonForChange" in caseDialog.histoCase.data.subjectId
                  ? caseDialog.histoCase.data.subjectId.reasonForChange
                  : ""
              }
              onChange={updateCaseFieldWithReasonForChange("subjectId")}
            />
          </RfcBox>
        ) : null}
        <Box mt={2}>
          <Label>
            Site ID
            <br />
            <TextInput
              placeholder="Site ID"
              value={caseDialog.histoCase.data.siteId.value || ""}
              onChange={updateCaseFieldWithValue("siteId")}
            />
            <Box
              style={{
                display: caseValidations.errorsExist ? "block" : "none",
                color: "red",
                fontSize: "10pt"
              }}
            >
              {caseValidations.siteIdLength}
              {caseValidations.siteIdLength ? <br /> : <></>}
              {caseValidations.siteIdFormat}
            </Box>
          </Label>
        </Box>
        {caseDialog.mode === CaseDialogMode.OpenForEdit &&
        caseDialog.savedHistoCase?.caseWithStatus.siteId !==
          caseDialog.histoCase.data.siteId.value ? (
          <RfcBox>
            <ReasonForChange
              rkey="siteId_reason"
              mb={2}
              value={
                "reasonForChange" in caseDialog.histoCase.data.siteId
                  ? caseDialog.histoCase.data.siteId.reasonForChange
                  : ""
              }
              onChange={updateCaseFieldWithReasonForChange("siteId")}
            />
          </RfcBox>
        ) : null}
        <Box mt={2}>
          <Label>
            Visit ID
            <br />
            <TextInput
              placeholder="Visit ID"
              value={caseDialog.histoCase.data.visitId.value}
              onChange={updateCaseFieldWithValue("visitId")}
            />
            <Box
              style={{
                display: caseValidations.errorsExist ? "block" : "none",
                color: "red",
                fontSize: "10pt"
              }}
            >
              {caseValidations.visitIdLength}
              {caseValidations.visitIdLength ? <br /> : <></>}
              {caseValidations.visitIdFormat}
            </Box>
          </Label>
        </Box>
        {caseDialog.mode === CaseDialogMode.OpenForEdit &&
        caseDialog.savedHistoCase?.caseWithStatus.visitId !==
          caseDialog.histoCase.data.visitId.value ? (
          <RfcBox>
            <ReasonForChange
              rkey="visitId_reason"
              mb={2}
              value={
                "reasonForChange" in caseDialog.histoCase.data.visitId
                  ? caseDialog.histoCase.data.visitId.reasonForChange
                  : ""
              }
              onChange={updateCaseFieldWithReasonForChange("visitId")}
            />
          </RfcBox>
        ) : null}
        <hr style={{ marginTop: "1.6rem" }} />
        <Box mb={2}>{readerSuggestionText}</Box>
        <Box display="block" justifyContent="space-between" mt={2}>
          <Box style={{ width: "100%" }}>
            <Label>
              Assign Readers
              {singleReaderLimitReached ? (
                <Text style={{ fontWeight: 400 }}>
                  This study does not use adjudication, so a case may have only one Central Reader.
                </Text>
              ) : null}
              <SelectReaders
                disabled={!readerPicker.canAdd}
                // The single-reader cap only blocks *adding* a second CR; the assigned CR must stay
                // removable (via its X) so the user can swap it for another (LUC-274). Removal is
                // still blocked when the user lacks the manual-assignment permission.
                deselectAlwaysEnabled={readerPicker.canRemove}
                placeholder={"Enter user’s name"}
                searchText={caseDialog.readersSearchText}
                searchResults={readerPool}
                onSelect={onSelectReader}
                onChangeSearchText={onReaderNameChange}
                onDeselect={onUnassignReader}
                selectedItems={
                  caseDialog.histoCase.data ? caseDialog.histoCase.data.readers.value : []
                }
                isLoading={
                  "isPending" in caseDialog.readersSearchResults
                    ? caseDialog.readersSearchResults.isPending
                    : false
                }
                format={formatReaderStats}
                getSearchLabel={stats => userLabelShort(stats.reader)}
              />
            </Label>
          </Box>
          {caseDialog.mode === CaseDialogMode.OpenForEdit &&
          caseDialog.savedHistoCase &&
          areDifferent(
            caseDialog.savedHistoCase.readers.map(readerStats => readerStats.reader.id),
            caseDialog.histoCase.data.readers.value.map(readerStats => readerStats.reader.id)
          ) ? (
            <RfcBox>
              <ReasonForChange
                rkey="readers_reason"
                mb={2}
                value={
                  "reasonForChange" in caseDialog.histoCase.data.readers
                    ? caseDialog.histoCase.data.readers.reasonForChange
                    : ""
                }
                onChange={updateCaseFieldWithReasonForChange("readers")}
              />
            </RfcBox>
          ) : null}
        </Box>
        {showAdjudicatorSection ? (
          <>
            <hr style={{ marginTop: "1.6rem" }} />
            <Box display="block" mb={2}>
              <Label>
                Assign Adjudicator
                <SelectAdjudicator
                  disabled={!canManuallyAssignReaders}
                  placeholder={"Enter adjudicator’s name"}
                  searchText={caseDialog.adjudicatorSearchText}
                  searchResults={adjudicatorPool}
                  onSelect={onSelectAdjudicator}
                  onChangeSearchText={onAdjudicatorNameChange}
                  onDeselect={onClearAdjudicator}
                  selectedItems={selectedAdjudicatorItems}
                  isLoading={
                    "isPending" in caseDialog.adjudicatorSearchResults
                      ? caseDialog.adjudicatorSearchResults.isPending
                      : false
                  }
                  format={formatReaderStats}
                  getSearchLabel={stats => userLabelShort(stats.reader)}
                />
              </Label>
              {isBiopsyLevel && adjudicatorForm.selected !== null ? (
                <Box mt={2}>
                  <Label>
                    Biopsies the adjudicator may access
                    <br />
                    <Text style={{ fontWeight: 400 }}>
                      Select the biopsies (images) this adjudicator will adjudicate.
                    </Text>
                  </Label>
                  {biopsies.length === 0 ? (
                    <Text style={{ fontWeight: 400 }}>No images on this case yet.</Text>
                  ) : (
                    biopsies.map((biopsy, index) => {
                      const selected = biopsy.imageIds.every(id =>
                        adjudicatorForm.selectedBiopsyImageIds.includes(id)
                      );
                      // Fall back to a positional label when the biopsy has no name, so at least
                      // something identifiable appears (index reflects the case's image ordering).
                      const displayName =
                        biopsy.name.trim() === "" ? `Image ${index + 1}` : biopsy.name;
                      return (
                        <Box
                          key={biopsy.imageIds.join(",")}
                          mb={1}
                          display="flex"
                          alignItems="center"
                        >
                          <Checkbox
                            checked={selected}
                            onChange={onToggleBiopsy(biopsy.imageIds)}
                            mr={1}
                          />
                          <Text style={{ fontWeight: 400 }}>{displayName}</Text>
                        </Box>
                      );
                    })
                  )}
                </Box>
              ) : null}
              {caseDialog.mode === CaseDialogMode.OpenForEdit &&
              caseDialog.savedHistoCase &&
              adjudicatorChanged ? (
                <RfcBox>
                  <ReasonForChange
                    rkey="adjudicator_reason"
                    mb={2}
                    value={adjudicatorRfc}
                    onChange={updateCaseFieldWithReasonForChange("adjudicator")}
                  />
                </RfcBox>
              ) : null}
            </Box>
          </>
        ) : null}
        <hr style={{ marginTop: "1.6rem" }} />
        <Box display="block" justifyContent="space-between" mb={2}>
          <Box width="100%">
            <Label>
              Assign Images
              <SelectImages
                placeholder={"Enter image file name"}
                searchText={caseDialog.imagesSearchText}
                searchResults={
                  "resource" in caseDialog.imagesSearchResults
                    ? caseDialog.imagesSearchResults.resource
                    : []
                }
                onSelect={onSelectImage}
                onChangeSearchText={onImageNameChange}
                onDeselect={onUnassignImage}
                selectedItems={
                  caseDialog.histoCase.data ? caseDialog.histoCase.data.images.value : []
                }
                isLoading={
                  "isPending" in caseDialog.imagesSearchResults &&
                  caseDialog.imagesSearchResults.isPending
                }
                format={prop("name")}
                disabled={imageAssignmentDisabled}
                disabledText={disabledText}
              />
            </Label>
          </Box>
          {caseDialog.mode === CaseDialogMode.OpenForEdit &&
          caseDialog.savedHistoCase &&
          areDifferent(
            caseDialog.savedHistoCase.images.map(image => image.id),
            caseDialog.histoCase.data.images.value.map(image => image.id)
          ) ? (
            <RfcBox>
              <ReasonForChange
                rkey="images_reason"
                mb={2}
                value={
                  "reasonForChange" in caseDialog.histoCase.data.images
                    ? caseDialog.histoCase.data.images.reasonForChange
                    : ""
                }
                onChange={updateCaseFieldWithReasonForChange("images")}
              />
            </RfcBox>
          ) : null}
        </Box>
      </DialogBody>
      <DialogFooter>
        <Button
          intent="primary"
          appearance="prominent"
          onClick={onSave}
          isLoading={"isSaving" in caseDialog.histoCase}
          disabled={disableSave}
        >
          Save
        </Button>
        <Button onClick={closeDialog}>Cancel</Button>
      </DialogFooter>
    </Dialog>
  );
};

export default CaseDialog;
